import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import StatsSection from './components/StatsSection';
import PlatformSection from './components/PlatformSection';
import SolutionsSection from './components/SolutionsSection';
import CustomersSection from './components/CustomersSection';
import HiringCTASection from './components/HiringCTASection';
import DemoCTASection from './components/DemoCTASection';
import Footer from './components/Footer';
import ScrollToTopButton from './components/ScrollToTopButton';
import DynamicMetaTags from './components/DynamicMetaTags';
import { usePreloadManager, useResourceHints } from './hooks/useLazyLoad';
import { preloader, ResourcePreloader } from './utils/ResourcePreloader';
import { getPreloadTier, canPreloadAggressively, getDeviceInfo } from './utils/preloadGate';
import { I18nProvider } from './i18n/I18nContext';
import { initIpTracking } from './track';
import { trackMetaSource } from './utils/metaDetection';

// CRITICAL: Capture ?meta=true IMMEDIATELY (synchronous, before any async code)
// This prevents race conditions where Router/Pixel might clean URL before we read it
trackMetaSource();

// Lazy load all non-homepage routes
// JsonAnimations und PlattformPage entfernt
const Angebotserstellung = lazy(() => import('./pages/Angebotserstellung'));
const UmlaufUndDienstplanung = lazy(() => import('./pages/UmlaufUndDienstplanung'));
const Disposition = lazy(() => import('./pages/Disposition'));
const FehlermeldungPage = lazy(() => import('./pages/FehlermeldungPage'));
const LohnabrechnungPage = lazy(() => import('./pages/LohnabrechnungPage'));
const VerkehrsverbuendePage = lazy(() => import('./pages/VerkehrsverbuendePage'));
const VerkehrsunternehmenPage = lazy(() => import('./pages/VerkehrsunternehmenPage'));
const FahrpersonalBindenPage = lazy(() => import('./pages/FahrpersonalBindenPage'));
const BetriebskostenSenkenPage = lazy(() => import('./pages/BetriebskostenSenkenPage'));
const FahrgastzufriedenheitPage = lazy(() => import('./pages/FahrgastzufriedenheitPage'));
const UeberUnsPage = lazy(() => import('./pages/UeberUnsPage'));
const KontaktPage = lazy(() => import('./pages/KontaktPage'));
const VeranstaltungenPage = lazy(() => import('./pages/VeranstaltungenPage'));
const KarrierePage = lazy(() => import('./pages/KarrierePage'));
const DatenschutzPage = lazy(() => import('./pages/DatenschutzPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const NutzungsbedingungenPage = lazy(() => import('./pages/NutzungsbedingungenPage'));
const DatenschutzAppPage = lazy(() => import('./pages/DatenschutzAppPage'));
const DataDashboardPage = lazy(() => import('./pages/DataDashboardPage'));

function HomePage() {
  // Mobile-freundliches Preloading - KEIN doppeltes Preloading!
  // useUniversalPreloading kümmert sich um alles

  return (
    <>
      <Header />
      <main className="bg-transparent">
        <HeroSection />
        <StatsSection />
        <PlatformSection />
        <SolutionsSection />
        <CustomersSection />
        <HiringCTASection />
        <DemoCTASection />
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}

function AngebotserstellungPage() {
  return (
    <>
      <Header />
      <Angebotserstellung />
      <Footer />
      <ScrollToTopButton />
    </>
  );
}

function UmlaufUndDienstplanungPage() {
  return (
    <>
      <Header />
      <UmlaufUndDienstplanung />
      <Footer />
      <ScrollToTopButton />
    </>
  );
}

function DispositionPage() {
  return (
    <>
      <Header />
      <Disposition />
      <Footer />
      <ScrollToTopButton />
    </>
  );
}

// Universelles Preloading-Setup
function useUniversalPreloading() {
  const hasPreloaded = useRef(false); // Verhindert mehrfaches Preloading bei Navigation

  useEffect(() => {
    // Nur einmal ausführen, nicht bei jeder Navigation
    if (hasPreloaded.current) return;
    hasPreloaded.current = true;

    // Importiere alle Bilder und den Preloader
    Promise.all([
      import('./utils/imageImports'),
      import('./utils/ResourcePreloader')
    ]).then(([{ imagesByRoute }, { preloader, ResourcePreloader }]) => {

      // Bestimme aktuelle Route für priorisiertes Laden
      const currentPath = window.location.pathname;
      let currentRouteKey = 'home';

      if (currentPath.includes('ueber-uns')) currentRouteKey = 'ueberuns';
      else if (currentPath.includes('angebotserstellung')) currentRouteKey = 'angebotserstellung';
      else if (currentPath.includes('umlauf')) currentRouteKey = 'umlauf';
      else if (currentPath.includes('disposition')) currentRouteKey = 'disposition';
      else if (currentPath.includes('fehlermeldung')) currentRouteKey = 'fehlermeldung';
      else if (currentPath.includes('lohnabrechnung')) currentRouteKey = 'lohnabrechnung';
      else if (currentPath.includes('fahrpersonal')) currentRouteKey = 'fahrpersonal';
      else if (currentPath.includes('fahrgastzufriedenheit')) currentRouteKey = 'fahrgastzufriedenheit';
      else if (currentPath.includes('betriebskosten')) currentRouteKey = 'betriebskosten';

      // 0. ALLERHÖCHSTE PRIORITÄT: Header-Video auf Homepage
      if (currentPath === '/' || currentPath === '') {
        // Auf der Homepage: Header-Video hat ABSOLUT höchste Priorität
        preloader.addResource('/header-animation_v4.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');

        // PlatformSection Videos mit HIGH Priority (near the fold)
        preloader.addResource('/videos/Platform.mp4', ResourcePreloader.PRIORITY.HIGH, 'video');
        preloader.addResource('/videos/Angebotserstellung.mp4', ResourcePreloader.PRIORITY.HIGH, 'video');
        preloader.addResource('/videos/Umlauf.mp4', ResourcePreloader.PRIORITY.HIGH, 'video');

        // Homepage Lottie Animationen sind jetzt lokal und werden automatisch von Webpack gebundelt
        // Kein separates Preloading mehr nötig - sie sind direkt im Bundle
      }

      // 1. Videos der aktuellen Seite SOFORT laden
      if (currentPath.includes('ueber-uns')) {
        preloader.addResource('/videos/BusAgentTeam.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }
      if (currentPath.includes('disposition')) {
        preloader.addResource('/videos/Disposition.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
        preloader.addResource('/videos/KrankMeldung.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }
      if (currentPath.includes('lohnabrechnung')) {
        preloader.addResource('/videos/lohnDashboard.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
        preloader.addResource('/videos/lohnDaten.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }
      if (currentPath.includes('umlauf')) {
        preloader.addResource('/videos/Disposition.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }
      if (currentPath.includes('fehlermeldung')) {
        preloader.addResource('/videos/Werkstatt.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }
      if (currentPath.includes('betriebskosten')) {
        preloader.addResource('/videos/GeldSparen.webm', ResourcePreloader.PRIORITY.CRITICAL, 'video');
      }

      // 2. Dann erst Bilder der aktuellen Seite
      const currentPageImages = imagesByRoute[currentRouteKey] || [];
      currentPageImages.forEach(imgUrl => {
        preloader.addResource(imgUrl, ResourcePreloader.PRIORITY.HIGH, 'image');
      });

      // Component Preloading basierend auf Geräte-Tier
      const tier = getPreloadTier();

      // Debug Info in Development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Device Info:', getDeviceInfo());
        console.log('🎯 Preload Strategy:', tier);
      }

      if (tier === 'veryHigh' || tier === 'high') {
        // High-End Geräte: Lade Top-3 Components gestaffelt
        (async () => {
          const sleep = (ms) => new Promise(r => setTimeout(r, ms));
          await sleep(2000);
          import('./pages/Angebotserstellung');
          await sleep(2000);
          import('./pages/UmlaufUndDienstplanung');
          await sleep(2000);
          import('./pages/Disposition');
        })();
      } else if (tier === 'mid') {
        // Mid-Tier: Nur die nächstwahrscheinliche Seite
        setTimeout(() => {
          import('./pages/Angebotserstellung');
        }, 3000);
      }
      // Low-Tier: Kein Component-Preloading, lazy loading reicht

      // Videos und Bilder: Nur wenn High-End Gerät
      if (tier === 'veryHigh' || tier === 'high') {
        setTimeout(() => {
          // Lade andere Bilder mit niedriger Priorität
          Object.keys(imagesByRoute).forEach(route => {
            if (route !== currentRouteKey) {
              const routeImages = imagesByRoute[route] || [];
              routeImages.forEach(imgUrl => {
                preloader.addResource(imgUrl, ResourcePreloader.PRIORITY.IDLE, 'image');
              });
            }
          });
        }, 3000);
      }
      // KEINE anderen Videos preloaden - nur aktuelle Seite!
    });
  }, []);
}

// Komponente für Route-basiertes Preloading
function AppContent() {
  const location = useLocation();

  // Aktiviere universelles Preloading auf JEDER Seite
  useUniversalPreloading();

  // Initialize IP-based tracking on first page load
  useEffect(() => {
    initIpTracking();
  }, []);

  // Bestimme aktuelle Route
  const getCurrentRoute = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.includes('angebotserstellung')) return 'angebotserstellung';
    if (path.includes('lohnabrechnung')) return 'lohnabrechnung';
    if (path.includes('umlauf-und-dienstplanung')) return 'umlauf';
    if (path.includes('disposition')) return 'disposition';
    if (path.includes('fehlermeldung')) return 'fehlermeldung';
    if (path.includes('betriebskosten')) return 'betriebskosten';
    if (path.includes('fahrpersonal')) return 'fahrpersonal';
    if (path.includes('fahrgastzufriedenheit')) return 'fahrgastzufriedenheit';
    if (path.includes('ueber-uns')) return 'ueberuns';
    return 'other';
  };

  // Verwende Preload Manager Hook
  usePreloadManager(getCurrentRoute());

  // Resource Hints für externe Ressourcen
  useResourceHints([
    { type: 'dns-prefetch', url: 'https://fonts.googleapis.com' },
    { type: 'preconnect', url: 'https://fonts.gstatic.com', crossorigin: true },
    // Füge weitere externe Domains hinzu falls benötigt
  ]);

  // Monitoring des Preloader Status (deaktiviert für saubere Console)
  useEffect(() => {
    // Deaktiviert um Console Spam zu vermeiden
    // if (process.env.NODE_ENV === 'development') {
    //   const interval = setInterval(() => {
    //     const status = preloader.getStatus();
    //     console.log('Preloader Status:', status);
    //   }, 5000);
    //
    //   return () => clearInterval(interval);
    // }
  }, []);

  return (
    <div className="App">
      <DynamicMetaTags />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Lädt...</div>
      </div>}>
        <Routes>
            {/* German routes */}
            <Route path="/de" element={<HomePage />} />
            <Route path="/de/produkte/angebotserstellung" element={<AngebotserstellungPage />} />
            <Route path="/de/produkte/umlauf-und-dienstplanung" element={<UmlaufUndDienstplanungPage />} />
            <Route path="/de/produkte/disposition" element={<DispositionPage />} />
            <Route path="/de/produkte/fehlermeldung" element={<FehlermeldungPage />} />
            <Route path="/de/produkte/lohnabrechnung" element={<LohnabrechnungPage />} />
            <Route path="/de/anwender/verkehrsverbuende-und-aufgabentraeger" element={<VerkehrsverbuendePage />} />
            <Route path="/de/anwender/verkehrsunternehmen" element={<VerkehrsunternehmenPage />} />
            <Route path="/de/anwender/fahrpersonal-binden" element={<FahrpersonalBindenPage />} />
            <Route path="/de/anwender/betriebskosten-senken" element={<BetriebskostenSenkenPage />} />
            <Route path="/de/anwender/steigerung-der-fahrgastzufriedenheit" element={<FahrgastzufriedenheitPage />} />
            <Route path="/de/unternehmen/ueber-uns" element={<UeberUnsPage />} />
            <Route path="/de/unternehmen/kontakt" element={<KontaktPage />} />
            <Route path="/de/unternehmen/kontakt/data" element={<DataDashboardPage />} />
            <Route path="/de/unternehmen/veranstaltungen" element={<VeranstaltungenPage />} />
            <Route path="/de/unternehmen/karriere" element={<KarrierePage />} />
            <Route path="/de/unternehmen/career" element={<KarrierePage />} />
            <Route path="/de/datenschutz" element={<DatenschutzPage />} />
            <Route path="/de/datenschutz/app" element={<DatenschutzAppPage />} />
            <Route path="/de/impressum" element={<ImpressumPage />} />
            <Route path="/de/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />

            {/* English routes */}
            <Route path="/en" element={<HomePage />} />
            <Route path="/en/produkte/angebotserstellung" element={<AngebotserstellungPage />} />
            <Route path="/en/produkte/umlauf-und-dienstplanung" element={<UmlaufUndDienstplanungPage />} />
            <Route path="/en/produkte/disposition" element={<DispositionPage />} />
            <Route path="/en/produkte/fehlermeldung" element={<FehlermeldungPage />} />
            <Route path="/en/produkte/lohnabrechnung" element={<LohnabrechnungPage />} />
            <Route path="/en/anwender/verkehrsverbuende-und-aufgabentraeger" element={<VerkehrsverbuendePage />} />
            <Route path="/en/anwender/verkehrsunternehmen" element={<VerkehrsunternehmenPage />} />
            <Route path="/en/anwender/fahrpersonal-binden" element={<FahrpersonalBindenPage />} />
            <Route path="/en/anwender/betriebskosten-senken" element={<BetriebskostenSenkenPage />} />
            <Route path="/en/anwender/steigerung-der-fahrgastzufriedenheit" element={<FahrgastzufriedenheitPage />} />
            <Route path="/en/unternehmen/ueber-uns" element={<UeberUnsPage />} />
            <Route path="/en/unternehmen/kontakt" element={<KontaktPage />} />
            <Route path="/en/unternehmen/veranstaltungen" element={<VeranstaltungenPage />} />
            <Route path="/en/unternehmen/karriere" element={<KarrierePage />} />
            <Route path="/en/unternehmen/career" element={<KarrierePage />} />
            <Route path="/en/datenschutz" element={<DatenschutzPage />} />
            <Route path="/en/datenschutz/app" element={<DatenschutzAppPage />} />
            <Route path="/en/impressum" element={<ImpressumPage />} />
            <Route path="/en/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />

            {/* Default routes without language prefix (fallback to German) */}
            <Route path="/" element={<HomePage />} />
            <Route path="/produkte/angebotserstellung" element={<AngebotserstellungPage />} />
            <Route path="/produkte/umlauf-und-dienstplanung" element={<UmlaufUndDienstplanungPage />} />
            <Route path="/produkte/disposition" element={<DispositionPage />} />
            <Route path="/produkte/fehlermeldung" element={<FehlermeldungPage />} />
            <Route path="/produkte/lohnabrechnung" element={<LohnabrechnungPage />} />
            <Route path="/anwender/verkehrsverbuende-und-aufgabentraeger" element={<VerkehrsverbuendePage />} />
            <Route path="/anwender/verkehrsunternehmen" element={<VerkehrsunternehmenPage />} />
            <Route path="/anwender/fahrpersonal-binden" element={<FahrpersonalBindenPage />} />
            <Route path="/anwender/betriebskosten-senken" element={<BetriebskostenSenkenPage />} />
            <Route path="/anwender/steigerung-der-fahrgastzufriedenheit" element={<FahrgastzufriedenheitPage />} />
            <Route path="/unternehmen/ueber-uns" element={<UeberUnsPage />} />
            <Route path="/unternehmen/kontakt" element={<KontaktPage />} />
            <Route path="/unternehmen/veranstaltungen" element={<VeranstaltungenPage />} />
            <Route path="/unternehmen/karriere" element={<KarrierePage />} />
            <Route path="/unternehmen/career" element={<KarrierePage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/datenschutz/app" element={<DatenschutzAppPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
          </Routes>
        </Suspense>
      </div>
  );
}

function App() {
  return (
    <Router>
      <I18nProvider>
        <AppContent />
      </I18nProvider>
    </Router>
  );
}

export default App;